import { useEffect, useMemo, useState } from "react";
import { imagesForColor, priceSummary } from "../../utils/storefrontVariants";
import { ArrowUpRight, Star } from "lucide-react";
import WishlistButton from "./WishlistButton";
import {productPath} from "../../utils/productLinks";
import { motion } from "framer-motion";
import { getProductStock } from "../../utils/storefrontStock";
import { storefrontProductTitle } from '../../utils/storefrontProductTitle';

const getImageUrl = (image) => {
  if (!image) return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${image.startsWith("/") ? "" : "/"}${image}`;
};

const getUniqueValues = (values, getKey) => {
  const seen = new Set();

  return values.filter((value) => {
    const key = getKey(value);

    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export const ProductCard = ({ item, navigate, index = 0 }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState("");
  useEffect(() => { setImageFailed(false); }, [item, selectedColorId]);

  const product = useMemo(() => {
    const { isInStock, availableQuantity, availableVariants } =
      getProductStock(item);

    const variants = item.variants || [];
    const variantsForDisplay =
      availableVariants.length > 0 ? availableVariants : variants;

    const displayVariants = selectedColorId ? variantsForDisplay.filter(v => v.color?.id === selectedColorId) : variantsForDisplay;
    const summary = priceSummary(displayVariants);
    const primaryVariant = summary.variant || displayVariants[0] || {};

    const price = Number(
      summary.variant ? summary.price : item.price ?? 0,
    );

    const mrp = Number(primaryVariant.mrp ?? item.mrp ?? price);

    const rawImages = [
      !selectedColorId && item.image,
      ...imagesForColor(item.images || [], variants, selectedColorId).map(
        (image) => image?.storageKey || image?.url || image?.imageKey,
      ),
    ].filter(Boolean);

    const image = getImageUrl(rawImages[0]);

    const colors = item.colors
      ? item.colors.map((color) => ({
          name: color?.name || color?.label || String(color),
          id: color?.id,
          value: color?.value || color?.hexCode || "#d1d5db",
        }))
      : getUniqueValues(
          variantsForDisplay
            .filter((variant) => variant.color)
            .map((variant) => ({
              name: variant.color.name,
              id: variant.color.id,
              value: variant.color.hexCode || "#d1d5db",
            })),
          (color) => color.name,
        );

    return {
      id: item.id,
      slug: item.slug,
      variantId: primaryVariant.id,
      title: storefrontProductTitle(item),
      articleNumber: item.productCode || '',
      varies: summary.varies,
      brand: item.brand?.name || item.brand || "CHARMS",
      price,
      mrp,
      image,
      colors,
      isInStock,
      availableQuantity: Number(availableQuantity || 0),
      badge:
        item.badge ||
        (item.isNewArrival ? "New arrival" : null) ||
        (item.isFeatured ? "Featured" : null),
      rating: item.rating,
    };
  }, [item, selectedColorId]);

  const discountPercent =
    product.mrp > product.price && product.price > 0
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  const isLowStock =
    product.isInStock &&
    product.availableQuantity > 0 &&
    product.availableQuantity <= 3;

  const openProduct = () => {
    navigate(productPath(product) + (selectedColorId ? `?color=${encodeURIComponent(selectedColorId)}` : ""));
  };

  return (
    <motion.article
      role="button"
      tabIndex={0}
      onClick={openProduct}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openProduct();
        }
      }}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="group relative min-h-[420px] cursor-pointer overflow-hidden bg-slate-950 text-left outline-none sm:min-h-[510px]"
    >
      {/* Product image */}
      {product.image && !imageFailed ? (
        <img
          src={product.image}
          alt={product.title}
          onError={() => setImageFailed(true)}
          className={`absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105 ${
            !product.isInStock ? "grayscale opacity-60" : ""
          }`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-950 via-primary-800 to-[#b15b6e] p-7 text-center">
          <span className="text-2xl font-semibold leading-tight text-white">
            {product.title}
          </span>
        </div>
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/5" />

      {/* Left badges */}
      <div className="absolute left-4 top-4 flex flex-col items-start gap-2">
        {!product.isInStock ? (
          <span className="bg-slate-950 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
            Sold out
          </span>
        ) : (
          <>
            {product.badge ? (
              <span className="bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-950">
                {product.badge}
              </span>
            ) : null}

            {discountPercent > 0 ? (
              <span className="bg-primary-800 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                {discountPercent}% off
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* Wishlist */}
      <WishlistButton
        variantId={product.variantId}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/15 text-white backdrop-blur-sm transition hover:scale-105 hover:bg-white hover:text-primary-800"
      />

      {/* Product information */}
      <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-6">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
              {product.brand}
            </p>

            <h3 className="mt-2 line-clamp-2 text-xl font-semibold leading-tight tracking-[-0.025em] sm:text-2xl">
              {product.title}
            </h3>
            {product.articleNumber && <p className="mt-1 text-xs text-white/80">Article No: {product.articleNumber}</p>}
          </div>

          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/60 bg-white/10 text-white backdrop-blur-sm transition duration-300 group-hover:rotate-45 group-hover:bg-white group-hover:text-slate-950">
            <ArrowUpRight size={18} />
          </span>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
          {product.price > 0 ? (
            <span className="text-lg font-bold sm:text-xl">
              {product.varies ? "From " : ""}{money(product.price)}
            </span>
          ) : (
            <span className="text-sm font-semibold">Price on request</span>
          )}

          {product.mrp > product.price && product.price > 0 ? (
            <span className="text-xs text-white/60 line-through">
              {money(product.mrp)}
            </span>
          ) : null}

          {product.rating ? (
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white/85">
              <Star size={13} className="fill-amber-400 text-amber-400" />
              {product.rating}
            </span>
          ) : null}
        </div>

        <div className="mt-4 flex min-h-5 items-center justify-between gap-3">
          {product.colors?.length > 0 ? (
            <div className="flex items-center gap-1.5">
              {product.colors.slice(0, 5).map((color) => (
                <button
                  type="button"
                  key={color.name}
                  aria-label={`View ${color.name}`}
                  aria-pressed={selectedColorId === color.id}
                  onClick={event => { event.stopPropagation(); if (color.id) setSelectedColorId(color.id); }}
                  title={color.name}
                  className={`h-5 w-5 rounded-full border border-white/60 ${selectedColorId === color.id ? "ring-2 ring-white ring-offset-2 ring-offset-black" : ""}`}
                  style={{ backgroundColor: color.value }}
                />
              ))}

              {product.colors.length > 5 ? (
                <span className="ml-1 text-[11px] text-white/75">
                  +{product.colors.length - 5}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-[11px] font-medium uppercase tracking-[0.13em] text-white/70">
              Explore product
            </span>
          )}

          {isLowStock ? (
            <span className="text-[11px] font-semibold text-white">
              Only {product.availableQuantity} left
            </span>
          ) : null}
        </div>
      </div>
    </motion.article>
  );
};
